import React from 'react';
import {
  VStack, HStack, Text, Checkbox, CheckboxIndicator, CheckboxIcon, CheckboxLabel, CheckIcon, Box, Spinner, Switch,
} from '@gluestack-ui/themed';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormField from '@/common/components/forms/FormField';
import SettingsGroupCard from '@/common/components/forms/SettingsGroupCard';
import { useTenantEventKinds } from '@/common/context/TenantSettingsStore';
import { useGetCourse } from '@/common/api/endpoints/courses/courses';
import type { CourseModule, User, CourseSegment } from '@/common/api/models';
import type { CustomFieldComponentProps } from '@/admin/common/EditMemberModal';
import useData from '../../../hooks/useData';

interface ModuleSettingsEditorData {
  courseSettings?: {
    segmentId?: string;
    ects?: number;
    learningHours?: number;
    enabledEventKinds?: string[];
    mandates?: {
      evaluation?: { id?: string };
      events?: Record<string, { id?: string }>;
    };
  };
}

/**
 * Props for ModuleSettingsEditor.
 *
 * Design Pattern: This component operates on LOCAL FORM STATE only.
 * It does NOT directly manipulate parent state during editing.
 * Changes are staged locally and committed only when the modal is saved.
 *
 * The parent can access staged changes via the value/onChange props from react-hook-form.
 */
interface ModuleSettingsEditorProps extends CustomFieldComponentProps<ModuleSettingsEditorData> {
  memberData: CourseModule & { displaySegmentId?: string };
  parentId: string; // This is the courseId
  /**
   * Read-only segments for dropdown choices.
   * This is NOT for bidirectional synchronization.
   */
  segments?: CourseSegment[];
}

/**
 * ModuleSettingsEditor component.
 *
 * Architecture: Follows the "Modal as Independent Editor" pattern.
 * - Works with LOCAL form state only (via react-hook-form)
 * - Does NOT mutate parent state directly
 * - Segment changes are stored in the form field 'courseSettings.segmentId'
 * - Parent reads the final state when modal is saved
 *
 * This ensures Cancel can safely discard all changes without side effects.
 */
const ModuleSettingsEditor: React.FC<ModuleSettingsEditorProps> = ({
  memberData,
  parentId,
  segments,
  // value and onChange are provided by CustomFieldComponentProps but not used
  // because this component works directly with react-hook-form's FormContext
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  value: _value,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange: _onChange,
}) => {
  const {
    control, watch, setValue, getValues,
  } = useFormContext();
  const { eventKinds } = useTenantEventKinds();
  const { t } = useTranslation();

  /**
   * Initialize form state from memberData when component mounts.
   * This ensures the internal form field 'segmentId' reflects the parent's curriculum order.
   *
   * Design Pattern: We sync on mount to ensure the dropdown shows the correct segment
   * computed by the parent form. We avoid including 'watch' in dependencies to
   * prevent overwriting user changes made in the dropdown.
   */
  React.useEffect(() => {
    const currentSegmentId = getValues('courseSettings.segmentId');
    const targetSegmentId = memberData.displaySegmentId;

    if (targetSegmentId && currentSegmentId !== targetSegmentId) {
      setValue('courseSettings.segmentId', targetSegmentId);
    }
  }, [memberData.displaySegmentId, setValue, getValues]);

  /**
   * Set default values for event kinds if not present (all checked by default).
   */
  React.useEffect(() => {
    const currentEnabled = getValues('courseSettings.enabledEventKinds');
    if (eventKinds.length > 0 && (!currentEnabled || currentEnabled.length === 0)) {
      setValue('courseSettings.enabledEventKinds', eventKinds.map((k) => k.id));
    }
  }, [eventKinds, setValue, getValues]);

  // Watch for overrides toggle
  const [showOverrides, setShowOverrides] = React.useState(
    !!watch('courseSettings.ects') || !!watch('courseSettings.learningHours'),
  );

  /**
   * Fetch course data to get segments.
   * Only fetches if segments were not provided as a prop (for better performance).
   */
  const { data: courseResponse, isLoading: isLoadingCourse } = useGetCourse(parentId, {
    query: {
      enabled: !segments, // Skip fetch if segments are provided
    },
  });
  const course = courseResponse?.data?.data;

  const { data: lecturers, isLoading: isLoadingLecturers } = useData({
    objectName: 'users',
    filter: { role: 'lecturer' },
    customQueryKey: 'lecturers',
    currentPageSize: 1000,
    currentPage: 1,
  });

  const lecturerChoices = React.useMemo(
    () => (lecturers as User[])?.map((l) => ({ label: `${l.lastname}, ${l.firstname}`, value: l.id })) || [],
    [lecturers],
  );

  /**
   * Segment choices for the dropdown.
   * Uses provided segments prop if available, otherwise falls back to fetched course data.
   */
  const segmentChoices = React.useMemo(() => {
    const sourceSegments = segments || (course?.segments as CourseSegment[]);
    return sourceSegments?.map((s) => ({ label: s.title, value: s.id })) || [];
  }, [segments, course]);

  if (isLoadingCourse || isLoadingLecturers) {
    return (
      <HStack space="sm" p="$10" justifyContent="center" alignItems="center">
        <Spinner size="small" color="$brandBase" />
        <Text size="xs" color="$textSubtle">{t('admin.course.modules.settings.loading', { defaultValue: 'Loading configuration...' })}</Text>
      </HStack>
    );
  }

  /**
   * Toggle handler for ECTS/Workload overrides.
   * Clears the override values when disabled.
   */
  const handleToggleOverrides = (val: boolean) => {
    setShowOverrides(val);
    if (!val) {
      setValue('courseSettings.ects', null);
      setValue('courseSettings.learningHours', null);
    }
  };

  return (
    <VStack space="lg" w="100%">
      <SettingsGroupCard title={t('admin.course.modules.settings.assignment_title', { defaultValue: 'Course Assignment' })}>
        <VStack space="lg">
          <HStack space="md" alignItems="flex-end" w="100%">
            <Box flex={1}>
              {/*
                Segment selection field.
                Changes are stored in the form field and will be read by the parent
                when the modal is saved. No direct parent state manipulation.
              */}
              <FormField
                name="courseSettings.segmentId"
                label={t('admin.course.modules.settings.segment_label', { defaultValue: 'Segment' })}
                type="select"
                choices={segmentChoices}
                placeholder={t('admin.course.modules.settings.segment_placeholder', { defaultValue: 'Select segment' })}
                fieldWidth="100%"
                labelAction={{
                  enabled: false,
                }}
              />
            </Box>
          </HStack>

          <VStack space="md" pt="$2" borderTopWidth={1} borderTopColor="$borderLight">
            <HStack justifyContent="space-between" alignItems="center">
              <Text size="sm" fontWeight="$medium">{t('admin.course.modules.settings.overload_toggle', { defaultValue: 'Overload module default ECTS/Workload?' })}</Text>
              <Switch
                size="sm"
                value={showOverrides}
                onToggle={handleToggleOverrides}
              />
            </HStack>

            {showOverrides && (
              <HStack space="md">
                <Box w={100}>
                  <FormField
                    name="courseSettings.ects"
                    label={t('admin.course.modules.settings.ects_label', { defaultValue: 'ECTS' })}
                    type="number"
                    fieldWidth="100%"
                  />
                </Box>
                <Box w={100}>
                  <FormField
                    name="courseSettings.learningHours"
                    label={t('admin.course.modules.settings.workload_label', { defaultValue: 'Workload' })}
                    type="number"
                    fieldWidth="100%"
                  />
                </Box>
              </HStack>
            )}
          </VStack>
        </VStack>
      </SettingsGroupCard>

      <SettingsGroupCard title={t('admin.course.modules.settings.meeting_types_title', { defaultValue: 'Include these Meeting Types' })}>
        <VStack space="sm">
          <HStack space="lg" flexWrap="wrap">
            {eventKinds.map((kind) => (
              <Controller
                key={kind.id}
                control={control}
                name="courseSettings.enabledEventKinds"
                render={({ field: { value = [], onChange } }) => (
                  <Checkbox
                    size="sm"
                    value={kind.id}
                    isChecked={!!(value as string[])?.includes(kind.id)}
                    onChange={(checked) => {
                      const newValue = checked
                        ? [...((value as string[]) || []), kind.id]
                        : ((value as string[]) || []).filter((id: string) => id !== kind.id);
                      onChange(newValue);
                    }}
                    aria-label={`Enable ${kind.label}`}
                  >
                    <CheckboxIndicator mr="$2">
                      <CheckboxIcon as={CheckIcon} />
                    </CheckboxIndicator>
                    <CheckboxLabel size="xs">{kind.label}</CheckboxLabel>
                  </Checkbox>
                )}
              />
            ))}
          </HStack>
        </VStack>
      </SettingsGroupCard>

      <SettingsGroupCard title={t('admin.course.modules.settings.personnel_title', { defaultValue: 'Responsible Personnel' })}>
        <Box flexWrap="wrap" flexDirection="row" w="100%">
          <Box w="50%" pr="$2" mb="$3">
            <FormField
              name="courseSettings.mandates.evaluation.id"
              label={t('admin.course.modules.settings.evaluation_label', { defaultValue: 'Evaluation' })}
              type="select"
              choices={lecturerChoices}
              fieldWidth="100%"
            />
          </Box>
          {eventKinds.map((kind) => (
            <Box key={kind.id} w="50%" pr="$2" mb="$3">
              <FormField
                name={`courseSettings.mandates.events.${kind.id}.id`}
                label={`${kind.label}`}
                type="select"
                choices={lecturerChoices}
                fieldWidth="100%"
              />
            </Box>
          ))}
        </Box>
      </SettingsGroupCard>
    </VStack>
  );
};

export default ModuleSettingsEditor;
